/**
 * VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
 * FOUNDATION_P0_V1 — the unified Billing Domain admin surface.
 *
 * Task spec §0 — the two confirmed UI designs ("套餐与积分" / "按量计费与
 * 充值") are ONE Billing Domain, never two independent systems — this is
 * why they are ONE route (`/platform/plan-credits`) with 5 internal
 * tabs (matching the confirmed UI's own 5-tab bar: 套餐管理 / 按量计费 ·
 * 积分充值 / 积分奖励规则 / 营销活动 / 积分流水), rather than two
 * separately-routed pages that would each need their own Plan/Currency
 * state. The two Sidebar entries ("套餐与积分" / "按量计费与充值" in
 * `navConfig.ts`) both point at this same route, differing only in which
 * tab they default to (`?tab=...`) — see `PLATFORM_NAV`.
 *
 * Task spec §6.2 — the selected Plan Key persists in `?plan_key=...` so
 * a refresh keeps the same Plan selected; the active tab persists in
 * `?tab=...` for the same reason (task spec §27's "T20 页面刷新数据保
 * 持").
 */
import { Typography } from 'antd'
import { useSearchParams } from 'react-router-dom'
import CreditLedgerTab from './billing/CreditLedgerTab'
import DailyRewardTab from './billing/DailyRewardTab'
import PlanManagementTab from './billing/PlanManagementTab'
import PromotionTab from './billing/PromotionTab'
import TopupPaygTab from './billing/TopupPaygTab'

const TABS = [
  { key: 'plans', label: '套餐管理' },
  { key: 'topup-payg', label: '按量计费 / 积分充值' },
  { key: 'daily-reward', label: '积分奖励规则' },
  { key: 'promotions', label: '营销活动' },
  { key: 'ledger', label: '积分流水' },
] as const

export default function PlanCreditsBillingPage() {
  const [params, setParams] = useSearchParams()
  const activeTab = TABS.some((t) => t.key === params.get('tab')) ? (params.get('tab') as string) : 'plans'
  const selectedPlanKey = params.get('plan_key')

  const setTab = (tab: string) => {
    const next = new URLSearchParams(params)
    next.set('tab', tab)
    setParams(next, { replace: true })
  }

  const setSelectedPlanKey = (key: string) => {
    const next = new URLSearchParams(params)
    next.set('plan_key', key)
    setParams(next, { replace: true })
  }

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          套餐与积分
        </Typography.Title>
        <Typography.Text type="secondary">
          管理积分套餐、按量计费、积分奖励规则和营销活动，配置 Vistoera 的积分经济体系。
        </Typography.Text>
      </div>

      <div className="pf-billing-tabbar" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--pf-border)', marginBottom: 14 }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setTab(tab.key)}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? 'var(--pf-primary)' : 'var(--pf-secondary-text)',
              borderBottom: activeTab === tab.key ? '2px solid var(--pf-primary)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'plans' && <PlanManagementTab selectedPlanKey={selectedPlanKey} onSelectPlan={setSelectedPlanKey} />}
      {activeTab === 'topup-payg' && <TopupPaygTab />}
      {activeTab === 'daily-reward' && <DailyRewardTab />}
      {activeTab === 'promotions' && <PromotionTab />}
      {activeTab === 'ledger' && <CreditLedgerTab />}
    </div>
  )
}
